import {
    fetchPreviewDocument,
    isDateRangeValid,
    sortSectionsByOrder,
} from '@app/helpers/cms.helpers';
import { CmsApiResponse, Hero, PromotedDestinations } from '@app/models';
import { CmsServiceBase } from '@app/services/abstracts/cms-service.base';

export class CmsHomepageService extends CmsServiceBase {
    private static _instance: CmsHomepageService;

    public static get instance(): CmsHomepageService {
        if (!this._instance) {
            this._instance = new CmsHomepageService();
        }

        return this._instance;
    }

    async getHeroSection(preview = false, documentId?: string): Promise<Hero | null> {
        const populate = {
            config: '*',
            content: {
                populate: ['imageSm', 'imageMd', 'imageLg'],
            },
        };

        try {
            if (preview && documentId) {
                return await fetchPreviewDocument<Hero>({
                    axiosInstance: this.axiosInstance,
                    endpoint: 'heroes',
                    documentId,
                    populate,
                    entityName: 'Hero',
                });
            }

            const params = this.buildStrapiParams({
                sort: 'createdAt:desc',
                filters: {
                    config: {
                        isActive: {
                            $eq: true,
                        },
                    },
                },
                publicationState: preview ? 'preview' : 'live',
                populate,
            });

            const response = await this.axiosInstance.get<CmsApiResponse<Hero[]>>('/heroes', {
                params,
            });

            const heroes = response.data.data;

            if (!heroes || heroes.length === 0) {
                return null;
            }

            const hero = heroes.find(hero =>
                isDateRangeValid(hero, h => !!h.config && !!h.content?.length),
            );

            return hero || null;
        } catch (error) {
            console.warn('Failed to fetch hero section:', error);
            return null;
        }
    }

    async getDestinationSuggestions(
        preview = false,
        documentId?: string,
    ): Promise<PromotedDestinations | null> {
        const populate = {
            config: '*',
            headline: '*',
            sections: {
                populate: {
                    icon: '*',
                    destinations: {
                        populate: ['image'],
                    },
                },
            },
        };

        try {
            if (preview && documentId) {
                return await fetchPreviewDocument<PromotedDestinations>({
                    axiosInstance: this.axiosInstance,
                    endpoint: 'promoted-destinations',
                    documentId,
                    populate,
                    entityName: 'Promoted destinations',
                    postProcess: data => {
                        sortSectionsByOrder(data);
                        return data;
                    },
                });
            }

            const params = this.buildStrapiParams({
                sort: 'createdAt:desc',
                filters: {
                    config: {
                        isActive: {
                            $eq: true,
                        },
                    },
                },
                publicationState: preview ? 'preview' : 'live',
                populate,
            });

            const response = await this.axiosInstance.get<CmsApiResponse<PromotedDestinations[]>>(
                '/promoted-destinations',
                {
                    params,
                },
            );

            const promotedDests = response.data.data;

            if (!promotedDests || promotedDests.length === 0) {
                return null;
            }

            const promotedDestinations = promotedDests.find(destination =>
                isDateRangeValid(destination, dest => !!dest.config && !!dest.sections?.length),
            );

            if (promotedDestinations) {
                sortSectionsByOrder(promotedDestinations);
            }

            return promotedDestinations || null;
        } catch (error) {
            console.warn('Failed to fetch destination suggestions:', error);
            return null;
        }
    }

    /**
     * Get Featured Accommodations data from CMS
     *
     * TODO: Create 'featured-accommodation' content type in Strapi with appropriate fields
     */
    async getFeaturedAccommodations(): Promise<any | null> {
        try {
            // TODO: Implement when Strapi content type is created

            return null;
        } catch (error) {
            console.warn('Failed to fetch featured accommodations:', error);
            return null;
        }
    }
}
